import React from 'react';
import { Target, Info } from 'lucide-react';
import { useBulleAuSurvol } from '../BulleSurvol';

/**
 * Les leviers d'audience d'un concurrent, et ce qu'on peut en conclure.
 *
 * ## ⚠️ Quatre états, et jamais un verdict
 *
 * C'est le cœur du dispositif, et la moitié qui se perd si on ne la tient pas :
 *
 * | État | Ce qu'on sait |
 * |---|---|
 * | **Confirmé** | relevé par plusieurs personnes : le hasard d'un panneau isolé est écarté |
 * | **Indice** | vu, pas assez — une piste, pas un fait |
 * | **Non activé** | jamais vu ALORS QUE le geste qui le déclenche est consigné depuis longtemps : **un constat** |
 * | **Non observé** | jamais vu, et rien n'a été tenté pour le déclencher : **on ne sait rien** |
 *
 * Les deux derniers se ressemblent et ne disent pas la même chose. « Je n'ai jamais vu de
 * retargeting site, et pourtant j'ai visité son site il y a trois semaines » est actionnable ;
 * « je n'ai rien relevé » ne l'est pas. Les confondre dans un seul « rien » ferait disparaître
 * le renseignement le plus utile du journal.
 *
 * ⚠️ **Un levier à zéro reste affiché**, en grisé : son absence est une information. Ne montrer
 * que les leviers vus ferait disparaître la question « et ceux qu'on n'a jamais vus ? ».
 *
 * ⚠️ **Les libellés et les aides viennent du serveur** (`useLeviersCiblage`), jamais d'une table
 * écrite ici — voir l'en-tête de `leviersServis.js`.
 */

/** Un repère par état. ⚠️ « Non observé » est gris : il ne dit rien, il ne doit rien suggérer. */
const APPARENCE = {
  confirme: { fond: '#ecfdf5', bord: '#a7f3d0', texte: '#065f46', point: '#10b981' },
  indice: { fond: '#fffbeb', bord: '#fde68a', texte: '#92400e', point: '#f59e0b' },
  absent_malgre_parcours: { fond: '#eff6ff', bord: '#bfdbfe', texte: '#1d4ed8', point: '#3b82f6' },
  non_observe: { fond: '#f9fafb', bord: '#e5e7eb', texte: '#9ca3af', point: '#d1d5db' }
};

const SyntheseCiblage = ({ synthese, familles, seuils }) => {
  const { survoler, quitter, bulle } = useBulleAuSurvol();

  if (!synthese) return null;

  const { leviers = [], resume } = synthese;

  // Les leviers vus d'abord, puis les constats d'absence, puis ce dont on ne sait rien : c'est
  // l'ordre dans lequel on les lit, et non l'ordre du registre.
  const rang = { confirme: 0, indice: 1, absent_malgre_parcours: 2, non_observe: 3 };
  const tries = [...leviers].sort((a, b) =>
    (rang[a.confiance.cle] ?? 9) - (rang[b.confiance.cle] ?? 9)
    || b.observations - a.observations
    || a.libelle.localeCompare(b.libelle));

  const nomFamille = (id) => familles.find(f => f.id === id)?.nom || id;

  return (
    <div style={{
      padding: '20px',
      borderRadius: '12px',
      backgroundColor: '#fff',
      border: '1px solid #e5e7eb',
      marginBottom: '20px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '4px' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', fontWeight: '600' }}>
          <Target size={16} />
          Leviers d'audience observés
        </h3>
        {resume && resume.observations > 0 && (
          <span style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
            {resume.observations} relevé{resume.observations > 1 ? 's' : ''} ·{' '}
            {resume.observateurs} observateur{resume.observateurs > 1 ? 's' : ''}
          </span>
        )}
      </div>
      <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
        D'après le panneau « Pourquoi je vois cette pub ? » — la seule source de cette information
      </p>

      {/* ⚠️ Un journal vide se dit, il ne se devine pas. Sans ce message, la liste grise des dix
          leviers « non observés » se lirait comme un défaut de chargement. */}
      {resume && resume.observations === 0 && (
        <div style={{
          padding: '14px 16px',
          borderRadius: '8px',
          backgroundColor: '#f9fafb',
          border: '1px dashed #d1d5db',
          fontSize: '13px',
          color: '#6b7280',
          lineHeight: 1.5,
          marginBottom: '16px'
        }}>
          Aucun relevé pour ce concurrent. Cette information n'existe nulle part ailleurs que
          dans le panneau « Pourquoi je vois cette pub ? », sur une publicité reçue : il faut
          l'ouvrir et la consigner. Commencez par déclarer un geste de parcours ci-dessous —
          c'est lui qui rendra les absences interprétables.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {tries.map(l => {
          const a = APPARENCE[l.confiance.cle] || APPARENCE.non_observe;
          const delaiMedian = l.delaiMedianHeures;
          return (
            <div
              key={l.levier}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 12px',
                borderRadius: '8px',
                backgroundColor: a.fond,
                border: `1px solid ${a.bord}`
              }}
            >
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: a.point, flexShrink: 0 }} />

              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  className={l.aide ? 'ksi-survolable' : undefined}
                  onMouseEnter={l.aide ? survoler(l.aide) : undefined}
                  onMouseLeave={l.aide ? quitter : undefined}
                  style={{ fontSize: '13px', fontWeight: '500', color: l.observations === 0 ? '#6b7280' : '#111827' }}
                >
                  {l.libelle}
                </div>
                <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                  {nomFamille(l.famille)}
                </div>
              </div>

              {/* Le décompte : les observations ET les observateurs. ⚠️ Le second est ce qui
                  distingue une corroboration d'une répétition, et le taire rendrait le niveau
                  de confiance inexplicable. */}
              {l.observations > 0 && (
                <span style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                  {l.observations} relevé{l.observations > 1 ? 's' : ''} · {l.observateurs} pers.
                </span>
              )}

              {/* ⚠️ `null` n'affiche rien, et surtout pas « 0 h » : aucune observation de ce
                  levier n'a de geste antérieur contre lequel se mesurer. */}
              {delaiMedian !== null && delaiMedian !== undefined && (
                <span
                  className="ksi-survolable"
                  onMouseEnter={survoler(
                    `Délai médian entre le geste de parcours et la publicité, sur `
                    + `${l.observationsAvecDelai} relevé${l.observationsAvecDelai > 1 ? 's' : ''} `
                    + `mesurable${l.observationsAvecDelai > 1 ? 's' : ''}.`
                  )}
                  onMouseLeave={quitter}
                  style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}
                >
                  ~{delaiMedian < 48 ? `${delaiMedian} h` : `${Math.round(delaiMedian / 24)} j`}
                </span>
              )}

              <span
                className="ksi-survolable"
                onMouseEnter={survoler(l.confiance.aide)}
                onMouseLeave={quitter}
                style={{
                  padding: '3px 10px',
                  borderRadius: '999px',
                  backgroundColor: '#fff',
                  border: `1px solid ${a.bord}`,
                  color: a.texte,
                  fontSize: '11px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap'
                }}
              >
                {l.confiance.libelle}
              </span>
            </div>
          );
        })}
      </div>

      {/* Le seuil, dit une fois : sans lui, « indice » sur trois relevés paraît arbitraire. */}
      {seuils && (
        <div style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'flex-start',
          marginTop: '16px',
          paddingTop: '14px',
          borderTop: '1px solid #f3f4f6',
          fontSize: '11px',
          color: '#9ca3af',
          lineHeight: 1.5
        }}>
          <Info size={13} style={{ flexShrink: 0, marginTop: '2px' }} />
          <span>
            Un levier est « confirmé » à partir de {seuils.observationsPourConfirmer} relevés
            par {seuils.observateursPourConfirmer} personnes différentes — la répétition d'un
            seul profil n'écarte pas la coïncidence. « Non activé » signifie que le geste qui
            déclencherait ce levier est consigné depuis plus de{' '}
            {seuils.joursPourConclureAbsence} jours sans qu'il soit jamais apparu ;
            « non observé » signifie qu'on n'en sait rien.
          </span>
        </div>
      )}

      {bulle}
    </div>
  );
};

export default SyntheseCiblage;
